import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCard } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BehaviorSubject, filter, take } from 'rxjs';
import { PolicyPrice, PolicyPriceColumns } from '../../policies/models/Policy';
import { Logger } from '../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { PriceRangeModalComponent } from '../components/price-range-modal/price-range-modal.component';
import { PriceRatesService } from '../services/price-rates.service';
import { PropertyManagerTableComponent } from './property-manager-table/property-manager-table.component';

@Component({
  selector: 'app-price-rates',
  templateUrl: './price-rates.component.html',
  styleUrls: ['./price-rates.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCard,
    MatIcon,
    MatTabsModule,
    MatTableModule,
    MatFormFieldModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    FormsModule,
    MatProgressSpinner,
    PropertyManagerTableComponent
  ]
})
export class PriceRatesComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) underwriterPaginator!: MatPaginator;
  @ViewChild(MatSort) underwriterSort: MatSort = new MatSort;

  loading = new BehaviorSubject<boolean>(false);
  underwriterDataSource = new MatTableDataSource<PolicyPrice>();
  underwriterDisplayedColumns = PolicyPriceColumns;
  underwriterTableLength = 0;
  underwriterPageSize = 10;
  underwriterPriceRates: PolicyPrice[] = [];
  displayedUnderwriterColumnNames: string[] = [];
  underwriterSearchKeyword: string = '';
  underwriterPageNumber = 1;

  initialSort = new BehaviorSubject<Sort | undefined>(undefined);
  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);
  initialKeyword = new BehaviorSubject<string>('');

  constructor(
    private router: Router,
    private priceRatesService: PriceRatesService,
    public userContextProvider: UserContextProvider,
    private logService: Logger,
    private dialog: MatDialog,
    private matSnackbar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.userContextProvider.isContextLoaded$.pipe(
      filter(loaded => loaded),
      take(1)
    ).subscribe(() => {
      this.initTableColumns();
      this.loadPage();
    });

    const storedItemsPerPage = localStorage.getItem('priceRatesItemsPerPage');
    if (storedItemsPerPage) {
      this.initialPageSize.next(parseInt(storedItemsPerPage, 10));
    } else {
      this.initialPageSize.next(10);
    }
  }

  ngAfterViewInit() {
    this.underwriterDataSource.paginator = this.underwriterPaginator;
    this.underwriterDataSource.sort = this.underwriterSort;

    this.initialPageSize?.subscribe(pageSize => {
      this.underwriterPageSize = pageSize;
      this.underwriterPaginator.pageSize = pageSize;
      this.cdr.detectChanges();
    });

    this.initialKeyword?.subscribe(keyword => {
      this.underwriterSearchKeyword = keyword;
      this.cdr.detectChanges();
    });

    this.initialPageNumber?.subscribe(pageNumber => {
      if (pageNumber != 1) {
        this.underwriterPageNumber = pageNumber;
        this.underwriterPaginator.pageIndex = pageNumber;
      } else {
        this.underwriterPageNumber = 1;
      }
      this.cdr.detectChanges();
    });

    this.initialSort?.subscribe(sort => {
      if (!sort) {
        return;
      }

      this.setSort(sort);
      this.cdr.detectChanges();
    });
  }

  setSort(sort: Sort) {
    this.underwriterSort.sort({ id: sort.active, start: sort.direction, disableClear: false });
  }

  initTableColumns(): void {
    if (this.underwriterDisplayedColumns === null) {
      this.underwriterDisplayedColumns = [];
    }
    this.displayedUnderwriterColumnNames = this.underwriterDisplayedColumns.map(c => c.column);
  }

  async loadPage() {
    try {
      this.loading.next(true);
      this.underwriterDataSource.data = [];

      const priceRates = await this.priceRatesService.getPriceRates();
      const mappedPriceRates = priceRates.map(p => this.buildPriceRateTableRow(p));

      this.underwriterPriceRates = mappedPriceRates.filter(priceRate => priceRate.type_id === 1);
      this.updateUnderwriterTableDataSource(this.underwriterPriceRates);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Price Rates', 'Error while retrieving Price Rates');
    } finally {
      this.loading.next(false);
    }
  }

  buildPriceRateTableRow(priceRate: PolicyPrice): any {
    const priceModel = this.priceRatesService.getPriceModelById(priceRate.pricing_model_id);

    return {
      id: priceRate.id,
      name: priceRate.name,
      pricing_model: priceModel,
      currency: priceRate.currency.code,
      price: priceRate.price ? priceRate.price : 0,
      deductible: priceRate.deductible ? priceRate.deductible : 0,
      type_id: priceRate.type_id,
      night_ranges: priceRate.night_ranges,
      extra_night_price: priceRate.extra_night_price
    }
  }

  updateUnderwriterTableDataSource(priceRates: PolicyPrice[]) {
    this.underwriterDataSource.data = priceRates;
    this.underwriterTableLength = priceRates.length;
  }

  onUnderwriterPageEvent(event: PageEvent): void {
    this.underwriterDataSource = new MatTableDataSource(this.underwriterPriceRates);
    this.underwriterPageSize = event.pageSize;
    const end = (event.pageIndex + 1) * this.underwriterPageSize;
    const start = event.pageIndex * this.underwriterPageSize;
    this.underwriterDataSource = new MatTableDataSource(this.underwriterDataSource.data.slice(start, end));
  }

  onUnderwriterSearchEvent(keyword: string): void {
    this.underwriterDataSource.filter = keyword.trim().toLowerCase();
  }

  onPageEvent(event: PageEvent): void {
    this.underwriterPageNumber = event.pageIndex + 1;
    localStorage.setItem('priceRatesItemsPerPage', event.pageSize.toString());
  }

  onViewRangesOpenClick(row: any) {
    this.dialog.open(PriceRangeModalComponent, {
      width: '70%',
      data: { row }
    });
  }

  onNewPriceRateClick() {
    this.router.navigate(['/price-rates/form']);
  }

  applyUnderwriterSearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.underwriterDataSource.filter = filterValue.trim().toLowerCase();

    if (this.underwriterDataSource.paginator) {
      this.underwriterDataSource.paginator.firstPage();
    }
  }

  clearUnderwriterSearch() {
    this.underwriterSearchKeyword = '';
    this.underwriterDataSource = new MatTableDataSource(this.underwriterPriceRates);
    this.ngAfterViewInit();
  }

  onGoToUnderwriterPageClick() {
    const max = Math.trunc(Math.ceil(this.underwriterPaginator.length / this.underwriterPaginator.pageSize));
    if (this.underwriterPageNumber < 1) {
      this.resetUnderwriterPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.underwriterPageNumber > max) {
      this.resetUnderwriterPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.underwriterPaginator.pageIndex = this.underwriterPageNumber - 1;
      this.underwriterPaginator.page.next({
        pageIndex: this.underwriterPageNumber - 1,
        pageSize: this.underwriterPaginator.pageSize,
        length: this.underwriterPaginator.length
      });
    }
  }

  resetUnderwriterPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.underwriterPageNumber = pageNumber;

    if (this.underwriterPaginator) {
      if (goToFirstPage) {
        this.underwriterPaginator.firstPage();
      } else {
        this.underwriterPaginator.lastPage();
      }
    }
  }

  displayInvalidPaginatorPageIndexError() {
    this.matSnackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }

  onRowClick(row: any): void {
    this.router.navigate([`/price-rates/detail/${row['id']}`]);
  }
}